import React from 'react'
import Box from '@material-ui/core/Box'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined'
import SyncAltIcon from '@material-ui/icons/SyncAlt'
import EventBusyOutlinedIcon from '@material-ui/icons/EventBusyOutlined'
import { makeStyles } from '@material-ui/core/styles'
import { DateTime } from 'luxon'
import BlockTimeDestroyDialog from '../../blocked-times/block-time-manager/block-time-destroy-dialog'
import { formatBlockRangeDate, formatTime12 } from './utils'

const GOOGLE_SYNCED_BLOCK_ID = '0'

const useStyles = makeStyles(theme => ({
  list: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 3,
    backgroundColor: '#F5F5F5',
    border: '1px solid #BDBDBD',
    boxSizing: 'border-box',
    display: 'inline-block',
    flexShrink: 0,
  },
  dotSynced: {
    backgroundColor: '#FFFFFF',
    backgroundImage:
      'repeating-linear-gradient(135deg, #EEEEEE 0 4px, #FFFFFF 4px 8px)',
    border: '1px dashed #BDBDBD',
  },
  date: {
    fontWeight: 500,
  },
  note: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  meta: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    marginLeft: 'auto',
    textAlign: 'right',
  },
  actions: {
    marginLeft: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  syncedIndicator: {
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(5, 2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(4, 2),
    },
  },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F5F5',
    color: '#616161',
    marginBottom: theme.spacing(1.5),
  },
  emptyTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  emptyCaption: {
    color: theme.palette.text.secondary,
    maxWidth: 480,
    marginBottom: theme.spacing(2),
  },
}))

const formatBlockTime = (startISO, endISO, allDay) => {
  if (allDay) return 'All day'
  return `${formatTime12(startISO)} – ${formatTime12(endISO)}`
}

const BlocksList = ({ blocks, onChanged, emptyAction }) => {
  const classes = useStyles()
  if (!blocks || blocks.length === 0) {
    return (
      <Box className={classes.empty}>
        <Box className={classes.emptyIcon}>
          <EventBusyOutlinedIcon style={{ fontSize: 28 }} />
        </Box>
        <Typography variant="subtitle1" className={classes.emptyTitle}>
          No Blocked Times
        </Typography>
        <Typography variant="body2" className={classes.emptyCaption}>
          Mark time as not bookable for vacations, time off, or other
          commitments so students can't book during those windows.
        </Typography>
        {emptyAction}
      </Box>
    )
  }

  const sorted = [...blocks].sort(
    (a, b) =>
      DateTime.fromISO(a.startTime).toMillis() -
      DateTime.fromISO(b.startTime).toMillis(),
  )

  return (
    <Box className={classes.list}>
      {sorted.map(block => {
        const timeText = formatBlockTime(
          block.startTime,
          block.endTime,
          block.allDay,
        )
        const note = block.blockNote
        const isSynced = block.id === GOOGLE_SYNCED_BLOCK_ID
        const cardKey = isSynced
          ? `synced-${block.startTime}-${block.endTime}`
          : block.id
        return (
          <Box key={cardKey} className={classes.row}>
            <span
              className={`${classes.dot} ${isSynced ? classes.dotSynced : ''}`}
              aria-hidden
            />
            <Box>
              <Typography variant="body2" className={classes.date}>
                {formatBlockRangeDate(block.startTime, block.endTime)}
              </Typography>
              {note && (
                <Typography variant="caption" className={classes.note}>
                  {note}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" className={classes.meta}>
              {timeText}
            </Typography>
            <Box className={classes.actions}>
              {isSynced ? (
                <Tooltip
                  title="Synced from your connected Google Calendar"
                  placement="left"
                >
                  <Box className={classes.syncedIndicator}>
                    <SyncAltIcon fontSize="small" />
                  </Box>
                </Tooltip>
              ) : (
                <BlockTimeDestroyDialog
                  blockTime={block}
                  onSuccess={onChanged}
                  trigger={
                    <IconButton size="small" aria-label="delete blocked time">
                      <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                  }
                />
              )}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default BlocksList
